<script setup>
import { socket, formatNumber, applySortAndFilter } from '@/helper'
import { onMounted, ref, onBeforeUnmount, computed, watch } from 'vue';
import { getRandomColor } from '@/helper';
import boardLoader from './boardLoader.vue';
import boardFilters from './boardFilters.vue';
import boardHero from './boardHero.vue';
import searchBar from './searchBar.vue';
import howTo from '../modals/howTo.vue';

import {Icon} from '@iconify/vue';
import { useHead } from '@vueuse/head';

const boardData = ref([]);

const sortBy = ref('');
const orderBy = ref(''); 
const searchTerm = ref('');
const loadingSearch = ref(false);
const loadingBoosted = ref(false);
const currentPage = ref(1);
const totalPages = ref(1);
const pageSize = 50; // Number of items per page
const isLoading = ref(false);
const boostedToken = ref({});
const isHowToModalOpen = ref(false);
const randomColor = ref([]);
const isBoostedFetched = ref(false);
const hasError = ref(false);

const openHowToModal = () => {
  isHowToModalOpen.value = true;
};

const handleUpdateSort = ({ field, order }) => {
    sortBy.value = field;
    orderBy.value = order;
    fetchBoardData();
  
};



const computedWidth = (token) => {
  return token?.percentComplete !== undefined ? `${token.percentComplete}%` : '0%';
};

const handleSearchTerm = (term) => {
    searchTerm.value = term;
    fetchBoardData(); // Fetch data with the search term
};

const fetchBoosted = async () => {
    try {
        loadingBoosted.value = true;
        const response = await fetch('http://localhost:3000/api/boosted');
        const data = await response.json();
        if (data.error === 'No boosted token found') {
            hasError.value = true;
            boostedToken.value = {};
            return;
        }
        boostedToken.value = data;
       
    } catch (error) {
        return;
    } finally {
        loadingBoosted.value = false;
    }
};

watch(loadingBoosted, (newVal) => {
  if (!newVal && boostedToken.value) {
    isBoostedFetched.value = true;
  }
});

const fetchBoardData = async () => {
    isLoading.value = true;
    loadingSearch.value = !!searchTerm.value;

    const queryParams = new URLSearchParams({
        sortBy: sortBy.value,
        order: orderBy.value,
        page: currentPage.value.toString(),
        limit: pageSize.toString(),
    });

    if (searchTerm.value) {
        queryParams.append('query', searchTerm.value);
    }

    try {
        const response = await fetch(`http://localhost:3000/api/board?${queryParams.toString()}`);
        const data = await response.json();
     
        boardData.value = data.response.map(token => ({
            ...token,
            isNew: false // Add isNew flag to each token
        }));
          
        totalPages.value = data.totalPages;
        randomColor.value = boardData.value.map(() => getRandomColor());
    } catch (error) {
        return;

    } finally {
        loadingSearch.value = false;
        isLoading.value = false;
    }
};

const nextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
        fetchBoardData();
    }
};

const previousPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
        fetchBoardData();
    }
};


onBeforeUnmount(() => {
  // Clean up WebSocket listeners
  socket.off('createEvent');

});

onMounted(() => {
    
    useHead({
        title: `WenMoon - Terminal`,
        meta: [],
    });

    fetchBoosted(); 
  socket.on('createEvent', (data) => {
    const updatedBoardData = [...boardData.value];
    updatedBoardData.unshift({
      ...data,
      isNew: true // Mark the newly created token with isNew flag
    });
    // Reapply sorting and filtering
    boardData.value = applySortAndFilter(updatedBoardData, sortBy.value, orderBy.value);
  });
  
 
});

const limitedPages = computed(() => {
  return [1, 2, 3];
});

function goToPage(page) {
  currentPage.value = page;
}

</script>
<template>
<div class="h-full">
    <div class="grid gap-8 px-3 mt-8 pb-5 mx-auto pb-16 max-w-[1600px]">
        <boardHero @openHowToModal="openHowToModal" :token="boostedToken" :hasBoosted="isBoostedFetched" :loading="loadingBoosted" :hasError="hasError"/>
    
        
        <div class="flex flex-col gap-4 mb-[10rem]">
            <div class="flex flex-col justify-center gap-3.5 lg:gap-0 lg:flex-row lg:justify-between">
                <searchBar @searchTerm="handleSearchTerm" :loading="loadingSearch"/>
                <boardFilters @updateSort="handleUpdateSort"/>    
            </div>
            <div v-if="!isLoading && boardData" class="grid grid-col-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 text-gray-800 gap-6 sm:gap-4">
                <router-link v-for="(token, index) in boardData" :key="token.id" :to="`/token/${token.mintAddress}`" class="relative w-full group bg-primary">
                    <div class="z-10 h-full overflow-hidden bg-primary grid border-gray-900 rounded-lg border-2 relative transition-all duration-300 ease-out transform translate-y-0 translate-x-0 group-hover:-translate-y-1.5 group-hover:-translate-x-1.5">
                        <div :class="{
                            'bg-teal-300': token.price_change > 0,
                            'bg-red-400': token.price_change < 0
                            }"
                            class="text-black absolute right-0 top-4 rounded-l-full w-fit text-[10px] py-1 px-2 bg-teal-300">
                        
                            <span v-if="token.price_change > 0">+</span>
                            <span v-else-if="token.price_change < 0">-</span>
                            <span class="ml-[1px] mr-[3px]">{{ token.price_change.toFixed(2) }}%</span>
                            <span v-if="token.price_change > 0">↑</span>
                            <span v-else-if="token.price_change < 0">↓</span>
                        </div>
                        <div class="flex flex-col p-4">
                           <div class="grid gap-2 h-full">
                                <div class="flex items-center mb-2">
                                    <img crossorigin="anonymous" alt="Token Image" loading="lazy" decoding="async" 
                                        class="object-cover w-10 h-10 rounded"
                                        :src="token.tokenImage">
                                    <div class="ml-2 text-gray-800 font-semibold max-w-[120px] truncate">
                                       <span> {{ token.tokenName }} </span>
                                    </div>
                                </div>
                                <div class="text-xs flex items-center gap-2 justify-between">
                                <div class="flex gap-2">
                                    Created by:
                                    <router-link :to="'/profile/' + token.user">
                                            <span v-if="token.user"
                                            class="rounded hover:underline flex max-w-[75px] text-moneyGreen underline">
                                            {{ token.user.substring(0, 5) }}...{{ token.user.substring(token.user.length - 5) }}
                                            </span>
                                    </router-link>
                                </div>
                                    <div class="block flex gap-4 ">
                                        <a v-if='token && token?.twitter !== ""' :href="token?.twitter" target="_blank" rel="noopener noreferrer" class=" text-gray-800 hover:underline"><Icon icon="fa6-brands:square-x-twitter" class="opacity-50 hover:opacity-100"/></a>
                                        <a v-if='token && token?.telegram !== ""' :href="token?.telegram" target="_blank" rel="noopener noreferrer" class=" text-gray-800 hover:underline"><Icon icon="bxl:telegram" class="opacity-50 hover:opacity-100"/></a>
                                        <a v-if='token && token?.website !== ""' :href="token?.website" target="_blank" rel="noopener noreferrer" class=" text-gray-800 hover:underline"><Icon icon="radix-icons:link-2" class="opacity-50 hover:opacity-100"/></a>
                                    </div>
                                </div>
                                <p class="text-sm w-full break-words">
                                    <span class="font-bold  tracking-tight">{{ token.tokenName }} (ticker: {{ token.tokenSymbol }}): </span>
                                </p>
                                <div class="overflow-scroll text-xs h-[69px]">
                                    {{ token.tokenDescription }}
                                </div>
                                <div class="text-xs mt-4 gap-3 grid">
                                    <p>Market Cap: 
                                        <span class="text-gray-800 bg-limen px-2 py-1 rounded-full font-medium">${{ formatNumber(token.marketCap_usd) }}</span>
                                        <span class="ml-1">({{ token.percentComplete }}%)</span>
                                    </p>
                                    <div aria-valuemax="100" aria-valuemin="0" role="progressbar" data-state="indeterminate" data-max="100" class="relative h-4 overflow-hidden rounded-full w-full bg-gray-200">
                                        <div
                                        data-state="indeterminate"
                                        data-max="100"
                                        class="h-full flex-1 bg-swirl transition-all"
                                        :style="{ width: computedWidth(token) }"
                                        ></div>
                                    </div>
                                </div>
                           </div>
                           
                        </div>
                    </div>
                    <span :style="{ backgroundColor: randomColor[index] }"
                    class="absolute bottom-0 right-0 w-full border-2 border-gray-900 h-full rounded-lg transition-all duration-300 ease-out"></span>
                </router-link> 
            </div> 
            <boardLoader v-else /> 
            <div v-if="boardData.length === 0">
                <div class="flex justify-center">
                    <div class="text-gray-800 text-md">
                        <span>No tokens found</span>
                    </div>
                </div>
            </div>  
            <div v-if="boardData.length > 50"
            class="w-full flex justify-center">
            <div class="w-full flex justify-center">
                <div class="flex text-gray-800 border-2 border-gray-900 rounded-lg bg-primary">
                    <button @click="previousPage" :disabled="currentPage === 1" class="text-sm border-r-2 border-gray-900 px-2 py-1" :class="[currentPage === 1 ? 'cursor-not-allowed' : '']">
                        Prev
                    </button>
                    <span v-for="page in limitedPages" :key="page" @click="goToPage(page)" 
                        class="ml-0"
                        :class="['text-sm px-2 py-1 cursor-pointer border-r-2 border-gray-900', { 'font-bold': page === currentPage }]">
                    {{ page }}
                    </span>
                    <button @click="nextPage" :disabled="currentPage === totalPages" class="text-sm cursor-not-allowed px-2 py-1" :class="[currentPage === totalPages ? 'cursor-not-allowed' : '']">
                        Next
                    </button>
                </div>
                </div>
            </div>
        </div>
    
    </div>
</div>
<howTo v-if="isHowToModalOpen" @close-modal="isHowToModalOpen = false" />
</template>
  
<script>
export default {
    name: 'HomePage',
    
};
</script>